import { FC, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import {
  defaultScheduleViewPreferences,
  fetchScheduleViewPreferences,
  updateScheduleViewPreferences,
  type ScheduleViewPreferences,
} from '../group-schedule/view-preferences-api';

const ScheduleViewPreferencesWorkspace: FC = () => {
  const [preferences, setPreferences] = useState<ScheduleViewPreferences>(defaultScheduleViewPreferences);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchScheduleViewPreferences(controller.signal)
      .then(setPreferences)
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load calendar preferences.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const saved = await updateScheduleViewPreferences(preferences);
      setPreferences(saved);
      setSuccess('Monthly schedule view preferences saved.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to save calendar preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="custom-card mb-0">
      <Card.Header>
        <div className="card-title mb-0">Monthly schedule view</div>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm" /> Loading view preferences.</div>
        ) : (
          <div className="d-flex flex-column gap-4">
            {error ? <Alert variant="warning" className="mb-0">{error}</Alert> : null}
            {success ? <Alert variant="success" className="mb-0">{success}</Alert> : null}

            <Form.Group>
              <Form.Label className="fw-semibold">Week columns</Form.Label>
              <div className="d-flex flex-wrap gap-2" role="group" aria-label="Monthly calendar week start">
                <Button
                  type="button"
                  variant={preferences.weekStart === 'sunday' ? 'primary' : 'outline-primary'}
                  onClick={() => setPreferences((current) => ({ ...current, weekStart: 'sunday' }))}
                >
                  Sunday–Saturday
                </Button>
                <Button
                  type="button"
                  variant={preferences.weekStart === 'monday' ? 'primary' : 'outline-primary'}
                  onClick={() => setPreferences((current) => ({ ...current, weekStart: 'monday' }))}
                >
                  Monday–Sunday
                </Button>
              </div>
              <Form.Text className="text-muted">Both choices retain all seven days; they only change which day begins each week.</Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Label className="fw-semibold">Default calendar density</Form.Label>
              <div className="d-flex flex-wrap gap-2" role="group" aria-label="Default monthly calendar density">
                <Button
                  type="button"
                  variant={preferences.density === 'standard' ? 'primary' : 'outline-primary'}
                  onClick={() => setPreferences((current) => ({ ...current, density: 'standard' }))}
                >
                  Standard names
                </Button>
                <Button
                  type="button"
                  variant={preferences.density === 'compact' ? 'primary' : 'outline-primary'}
                  onClick={() => setPreferences((current) => ({ ...current, density: 'compact' }))}
                >
                  Compact initials
                </Button>
              </div>
            </Form.Group>

            <div>
              <Button type="button" variant="primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save view preferences'}
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ScheduleViewPreferencesWorkspace;
